import { Center, Loader, Text } from '@mantine/core';
import { needsName } from '@reditelji/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { ApiRequestError, setCsrfToken } from '../api/client';
import { useEvent, useTimeline } from '../api/hooks';
import { ME_QUERY_KEY, useMe, useSession } from '../api/session';
import { useChangeStream } from '../api/sse';
import { ChangelogDrawer } from '../changelog/ChangelogDrawer';
import { AppHeader } from '../layout/AppHeader';
import { NameDialog } from '../layout/NameDialog';
import { setErrorTrackingUser } from '../lib/errorTracking';
import { useUi } from '../state/ui';
import { readClock } from '../live/clock';

/** Auth guard + global chrome for all coordinator screens. */
export function CoordinatorLayout() {
  const navigate = useNavigate();
  const me = useMe();
  const { can } = useSession();
  const event = useEvent();
  const { selectedDayId, setSelectedDay, filters, toggleFilter } = useUi();
  const timeline = useTimeline(selectedDayId);

  useChangeStream(Boolean(me.data));

  // A role changed on Uporabniki changes what the payloads carry, not only
  // what may be drawn: every cached screen is dropped and read again, so
  // correspondence does not linger for an organizer, nor read as missing for
  // somebody promoted.
  const qc = useQueryClient();
  const role = me.data?.role;
  const shownRole = useRef(role);
  useEffect(() => {
    if (!role) return;
    if (shownRole.current && shownRole.current !== role) {
      void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== ME_QUERY_KEY[0] });
    }
    shownRole.current = role;
  }, [role, qc]);

  // The board's "brez brifinga" chip is not drawn without comms:read, so a
  // login whose role lost it while the chip was on would be left filtering to
  // nothing with no way to switch it off.
  const briefingFilterStuck = Boolean(me.data) && !can('comms:read') && filters.has('brifing');
  useEffect(() => {
    if (briefingFilterStuck) toggleFilter('brifing');
  }, [briefingFilterStuck, toggleFilter]);

  useEffect(() => {
    if (me.data) {
      setCsrfToken(me.data.csrfToken);
      setErrorTrackingUser(me.data.id);
    }
    if (me.error instanceof ApiRequestError && me.error.status === 401) {
      setErrorTrackingUser(null);
      void navigate('/login');
    }
  }, [me.data, me.error, navigate]);

  // Open on today when the event is on, the first day otherwise — on race
  // morning every screen has to start where the day is.
  useEffect(() => {
    const days = event.data?.days;
    if (!days?.length || selectedDayId) return;
    const today = readClock().isoDate;
    setSelectedDay((days.find((d) => d.date === today) ?? days[0]!).id);
  }, [event.data, selectedDayId, setSelectedDay]);

  if (me.isPending) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }
  if (!me.data) return null;

  return (
    <>
      <AppHeader me={me.data} timeline={timeline.data} />
      {/* Everything a coordinator does is signed with their name, so a login
          without one says who it is before anything else. */}
      {needsName(me.data) ? <NameDialog current="" required /> : null}
      <Outlet />
      {/* The drawer marks the Dnevnik seen when it opens — a write of its own. */}
      {can('audit:read') ? <ChangelogDrawer /> : null}
    </>
  );
}

export function PlaceholderPage({ title, phase }: { title: string; phase: number }) {
  return (
    <Center mih="60vh">
      <div style={{ textAlign: 'center' }}>
        <Text fw={700} size="lg">
          {title}
        </Text>
        <Text size="sm" c="dimmed">
          Ta zaslon pride v fazi {phase}.
        </Text>
      </div>
    </Center>
  );
}
